import { useTheme } from '../../contexts/ThemeContext';

interface ButtonProps {
  variant?: 'primary' | 'secondary';
  onClick?: () => void;
  disabled?: boolean;
  children: React.ReactNode;
  style?: React.CSSProperties;
}

export default function Button({ variant = 'primary', onClick, disabled, children, style }: ButtonProps) {
  const { theme } = useTheme();
  
  const baseStyle: React.CSSProperties = {
    padding: '10px 20px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    border: 'none',
    position: 'relative',
    ...style
  };

  const variantStyles = {
    primary: {
      background: theme.colors.brand,
      color: theme.colors.textPrimary,
      boxShadow: `0 2px 8px ${theme.colors.brand}40`,
    },
    secondary: {
      background: 'transparent',
      border: `1px solid ${theme.colors.border}`,
      color: theme.colors.textPrimary,
    }
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{ ...baseStyle, ...variantStyles[variant] }}
      onMouseEnter={(e) => {
        if (!disabled && variant === 'primary') {
          e.currentTarget.style.background = theme.colors.brandHover;
          e.currentTarget.style.boxShadow = `0 4px 12px ${theme.colors.brand}60`;
        } else if (!disabled && variant === 'secondary') {
          e.currentTarget.style.background = theme.colors.hover;
          e.currentTarget.style.borderColor = theme.colors.brand;
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled && variant === 'primary') {
          e.currentTarget.style.background = theme.colors.brand;
          e.currentTarget.style.boxShadow = `0 2px 8px ${theme.colors.brand}40`;
        } else if (!disabled && variant === 'secondary') {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.borderColor = theme.colors.border;
        }
      }}
    >
      {/* Glossy overlay for primary buttons */}
      {variant === 'primary' && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '50%',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%)',
          pointerEvents: 'none',
          borderRadius: '8px 8px 0 0',
          zIndex: 0
        }} />
      )}
      <span style={{ position: 'relative', zIndex: 1 }}>
        {children}
      </span>
    </button>
  );
}
